import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/hooks/useAuth";
import {
  DEFAULT_SIGNING_SORT,
  EMPTY_SIGNING_FILTER,
  filterSigningItems,
  isBulkSelectable,
  isSigningFilterActive,
  resolveSelection,
  signersOf,
  sortSigningItems,
  type SigningFilterValue,
  type SigningSort,
  type SigningSortField,
} from "./status";
import type { SigningListItem } from "./types";

export type SigningTab = "mine" | "all";

/**
 * Everything the Signing page holds locally: which tab, search box, filter,
 * sort and selected row — plus the list those produce. Extracted from the page
 * so the page component stays a thin composition.
 */
export function useSigningPageState(items: SigningListItem[]) {
  const { userProfile } = useAuth();
  const currentUserId = userProfile?.id ?? null;

  const [tab, setTab] = useState<SigningTab>("all");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<SigningFilterValue>(EMPTY_SIGNING_FILTER);
  const [sort, setSort] = useState<SigningSort>(DEFAULT_SIGNING_SORT);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // The multi-selection, separate from `selectedKey`: that one drives the
  // detail panel, this one the bulk action bar.
  const [checkedKeys, setCheckedKeys] = useState<ReadonlySet<string>>(() => new Set());

  const signers = useMemo(() => signersOf(items), [items]);

  const visibleItems = useMemo(
    () =>
      sortSigningItems(
        filterSigningItems(items, { search, filter, mineOnly: tab === "mine", currentUserId }),
        sort
      ),
    [items, search, filter, tab, currentUserId, sort]
  );

  // The sheet is the user's to close. It resolves against EVERY loaded row,
  // not just the visible ones: the list polls, and a chain whose status moves
  // past an active filter (say, right after the user signs it in the sheet)
  // must not slide out from under them. Only a row that has left the data
  // altogether drops the selection — dropped, not kept in reserve, so it
  // cannot spring back open later.
  const selectedItem = resolveSelection(items, selectedKey);
  // An empty list says nothing about the selection — a project switch or a
  // refetch can pass through one — so only a loaded list that lacks the row
  // drops it.
  useEffect(() => {
    if (selectedKey !== null && selectedItem === null && items.length > 0) setSelectedKey(null);
  }, [selectedKey, selectedItem, items.length]);

  const toggleSort = (field: SigningSortField) => setSort((prev) => nextSort(prev, field));

  // What the bulk bar acts on: only rows that are on screen right now. A row a
  // filter or search hid stays checked and comes back checked, but an action
  // never reaches a file the user cannot see.
  const checkableItems = useMemo(() => visibleItems.filter(isBulkSelectable), [visibleItems]);
  const checkedItems = useMemo(
    () => checkableItems.filter((item) => checkedKeys.has(item.key)),
    [checkableItems, checkedKeys]
  );

  const setChecked = (key: string, checked: boolean) =>
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });

  const setAllChecked = (checked: boolean) =>
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      for (const item of checkableItems) {
        if (checked) next.add(item.key);
        else next.delete(item.key);
      }
      return next;
    });

  return {
    tab,
    setTab,
    search,
    setSearch,
    filter,
    setFilter,
    sort,
    toggleSort,
    signers,
    visibleItems,
    /** A search term or filter is hiding rows, rather than there being none. */
    isFiltered: isSigningFilterActive(filter) || search.trim().length > 0,
    selectedItem,
    selectItem: setSelectedKey,
    checkedKeys,
    checkedItems,
    /** Visible rows that can be checked — drives the header checkbox. */
    checkableCount: checkableItems.length,
    setChecked,
    setAllChecked,
    clearChecked: () => setCheckedKeys(new Set()),
  };
}

export type SigningPageState = ReturnType<typeof useSigningPageState>;

/** Re-clicking a column flips its direction; a new column starts fresh. */
function nextSort(prev: SigningSort, field: SigningSortField): SigningSort {
  if (prev.field === field) {
    return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
  }
  return { field, direction: field === "created" ? "desc" : "asc" };
}
